/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { squareAPI } from '../services/api';
import SquarePaymentModal from './SquarePaymentModal';
import Icon from './Icon';

/**
 * Shows services/tickets that are ready for billing or pending payment.
 * Payment is processed via the standalone SquarePaymentModal.
 */
export default function SquarePendingOrders({ darkMode = true }) {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [paymentContext, setPaymentContext] = useState(null);
    const [paymentOpen, setPaymentOpen] = useState(false);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const res = await squareAPI.listPendingOrders();
            setOrders(res.data?.orders || []);
            setError('');
        } catch (err) {
            console.error(err);
            if (err.response?.status !== 403) {
                setError('Failed to load pending services');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
        const interval = setInterval(fetchOrders, 30000);
        return () => clearInterval(interval);
    }, []);

    const handleProcess = (order) => {
        setPaymentContext({
            orderId: order.orderId || order.square_order_id,
            serviceId: order.serviceId || order.service_id,
            amountCents: order.amount || order.total_amount_cents || 0,
            currency: order.currency || 'GBP',
            frameNumber: order.frameNumber || order.frame_number || '',
            locationId: order.squareLocationId || order.square_location_id,
            ticketName: order.ticket_name || '',
        });
        setPaymentOpen(true);
    };

    const statusColor = (status) => {
        if (status === 'OPEN') return '#d97706';
        if (status === 'COMPLETED') return '#059669';
        if (status === 'CANCELED') return '#ef4444';
        return '#6b7280';
    };

    const containerStyle = {
        background: darkMode ? '#1f2937' : '#ffffff',
        color: darkMode ? '#f9fafb' : '#111827',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '20px',
        border: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #e5e7eb'
    };

    if (!loading && orders.length === 0 && !error) {
        return null;
    }

    return (
        <div style={containerStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon name="warning" customColor="#f59e0b" size={20} /> Services ready for payment
                </h3>
                <button
                    onClick={fetchOrders}
                    disabled={loading}
                    style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: darkMode ? '#9ca3af' : '#6b7280', display: 'flex', alignItems: 'center', gap: '6px',
                        fontSize: '13px', opacity: loading ? 0.7 : 1
                    }}
                >
                    <Icon name="refresh" size={16} className={loading ? 'spin' : ''} /> {loading ? 'Refreshing...' : 'Refresh'}
                </button>
            </div>

            {error && <p style={{ fontSize: '13px', color: '#ef4444', marginBottom: '10px' }}>{error}</p>}

            {orders.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                            <tr style={{ borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`, textAlign: 'left' }}>
                                <th style={{ padding: '8px', color: '#9ca3af', fontWeight: 500 }}>Date</th>
                                <th style={{ padding: '8px', color: '#9ca3af', fontWeight: 500 }}>Ticket / Bike</th>
                                <th style={{ padding: '8px', color: '#9ca3af', fontWeight: 500 }}>Amount</th>
                                <th style={{ padding: '8px', color: '#9ca3af', fontWeight: 500 }}>Status</th>
                                <th style={{ padding: '8px', textAlign: 'right', color: '#9ca3af', fontWeight: 500 }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map(order => (
                                <tr key={order.sessionId} style={{ borderBottom: `1px solid ${darkMode ? '#374151' : '#f3f4f6'}` }}>
                                    <td style={{ padding: '10px 8px' }}>
                                        {new Date(order.createdAt || order.created_at).toLocaleDateString()}
                                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                            {new Date(order.createdAt || order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </td>
                                    <td style={{ padding: '10px 8px' }}>
                                        {order.ticket_name && (
                                          <div style={{ fontWeight: 600, color: darkMode ? '#60a5fa' : '#2563eb', marginBottom: '2px' }}>
                                            {order.ticket_name}
                                          </div>
                                        )}
                                        {(order.frameNumber || order.frame_number) && (
                                          <div style={{ fontSize: '11px', color: darkMode ? '#9ca3af' : '#6b7280' }}>
                                            Frame: {order.frameNumber || order.frame_number}
                                          </div>
                                        )}
                                    </td>
                                    <td style={{ padding: '10px 8px', fontWeight: 600 }}>
                                        {((order.amount || order.total_amount_cents || 0) / 100).toFixed(2)} {order.currency || 'GBP'}
                                    </td>
                                    <td style={{ padding: '10px 8px' }}>
                                        <span style={{
                                            padding: '2px 8px', borderRadius: '99px', fontSize: '10px', fontWeight: 600,
                                            background: statusColor(order.status || order.order_status) + '20',
                                            color: statusColor(order.status || order.order_status),
                                            border: `1px solid ${statusColor(order.status || order.order_status)}40`
                                        }}>
                                            {order.status || order.order_status || 'PENDING'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                                        <button
                                            onClick={() => handleProcess(order)}
                                            style={{
                                                padding: '6px 12px',
                                                background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px',
                                                cursor: 'pointer', fontSize: '12px', fontWeight: 500,
                                                display: 'inline-flex', alignItems: 'center', gap: '6px'
                                            }}
                                        >
                                            <Icon name="credit_card" customColor="#ffffff" size={16} /> Process payment
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <SquarePaymentModal
                isOpen={paymentOpen}
                paymentContext={paymentContext}
                onClose={() => { setPaymentOpen(false); setPaymentContext(null); fetchOrders(); }}
                onPaid={() => { setPaymentOpen(false); setPaymentContext(null); fetchOrders(); }}
                darkMode={darkMode}
            />
        </div>
    );
}
